import { useCallback, useMemo, useEffect } from 'react';

import { DEPARTING, RETURNING } from './constants';
import moment from '@g4/moment-utc';
import { useMedia } from '@g4/boneyard/lib/Media';

const useTabDates = (searchCriteria, tabDates, setTabDates) => {
  const mq = useMedia();
  const departDate = searchCriteria?.departDate?.date;
  const returnDate = searchCriteria?.returnDate?.date;

  const initialTabDates = useMemo(
    () => getInitialTabDates(departDate, returnDate, mq),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [departDate, returnDate]
  );

  useEffect(() => {
    if (tabDates[DEPARTING]?.selected === null) {
      setTabDates(initialTabDates);
    }
  }, [tabDates, initialTabDates, setTabDates]);

  const updateTabDates = useCallback(
    ({ [DEPARTING]: departingTabs, [RETURNING]: returningTabs }) => {
      setTabDates(validateNewTabs(departingTabs, returningTabs, tabDates));
    },
    [tabDates, setTabDates]
  );

  const setDepartingTabs = useCallback(
    newDepartingTabs => {
      updateTabDates({ [DEPARTING]: newDepartingTabs });
    },
    [updateTabDates]
  );

  const setReturningTabs = useCallback(
    newReturningTabs => {
      if (newReturningTabs.middle === 'Invalid date') {
        const newReturningDate = moment(tabDates?.departing?.selected).add(
          1,
          'd'
        );
        const updatedNewReturningTabs = {
          middle: newReturningDate,
          selected: newReturningDate,
        };
        updateTabDates({ [RETURNING]: updatedNewReturningTabs });
      } else {
        updateTabDates({ [RETURNING]: newReturningTabs });
      }
    },
    [updateTabDates, tabDates]
  );

  return {
    updateTabDates,
    setDepartingTabs,
    setReturningTabs,
  };
};

const validateNewTabs = (departingTabs, returningTabs, currentTabDates) => {
  if (!departingTabs && !returningTabs) {
    // This shouldn't run if there are no tabs to update,
    // but just in case, we can return the original state.
    return currentTabDates;
  }
  const currentDate = moment().format('YYYY-MM-DD');

  const validTabDates = {
    [DEPARTING]: {
      selected: validateDate(
        departingTabs?.selected,
        currentTabDates[DEPARTING].selected,
        currentDate
      ),
      middle: validateDate(
        departingTabs?.middle,
        currentTabDates[DEPARTING].middle,
        currentDate
      ),
    },
    [RETURNING]: {
      selected: validateDate(
        returningTabs?.selected,
        currentTabDates[RETURNING].selected,
        currentDate
      ),
      middle: validateDate(
        returningTabs?.middle,
        currentTabDates[RETURNING].middle,
        currentDate
      ),
    },
  };

  if (!validTabDates[RETURNING].middle) {
    validTabDates[RETURNING].middle = validTabDates[RETURNING].selected;
  }
  if (!validTabDates[DEPARTING].middle) {
    validTabDates[DEPARTING].middle = validTabDates[DEPARTING].selected;
  }

  return validTabDates;
};

const validateDate = (newDate, tabDate, minimumLimit) => {
  if (!newDate && !tabDate) {
    return null;
  }
  let date = newDate;
  if (!date) {
    // not specified, use current value.
    date = tabDate;
  }
  if (moment(date).isBefore(minimumLimit)) {
    date = minimumLimit;
  }
  return moment(date).format('YYYY-MM-DD');
};

const getInitialTabDates = (departDate, returnDate, mq) => {
  let initialReturnMiddleDate = returnDate;
  if (
    returnDate &&
    moment(returnDate).diff(moment(departDate), 'days') < 7 &&
    mq.gt('sm')
  ) {
    initialReturnMiddleDate = moment(departDate)
      .add(3, 'day')
      .format('YYYY-MM-DD');
  }
  return {
    [DEPARTING]: {
      selected: departDate,
      middle: departDate,
    },
    [RETURNING]: {
      selected: returnDate,
      middle: initialReturnMiddleDate,
    },
  };
};

export default useTabDates;
